import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  Divider,
} from '@mui/material';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { subscribeConnectionStatus } from '../../services/socket';
import {
  Dashboard as DashboardIcon,
  AccountCircle as AccountCircleIcon,
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  PersonAdd as PersonAddIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CardGiftcard as CardGiftcardIcon,
  Person as PersonIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as AutoModeIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/auth.store';
import { useThemeMode } from '../../theme';

const DRAWER_WIDTH = 240;

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { mode, cycleMode } = useThemeMode();

  const menuItems = [
    { text: 'Дашборд', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'VK Аккаунты', icon: <AccountCircleIcon />, path: '/accounts' },
    { text: 'Автолайкер', icon: <FavoriteIcon />, path: '/auto-liker' },
    { text: 'Автокомментатор', icon: <CommentIcon />, path: '/auto-commenter' },
    { text: 'Автоподписчик', icon: <PersonAddIcon />, path: '/auto-subscriber' },
    { text: 'История', icon: <HistoryIcon />, path: '/history' },
    { text: 'Подписка', icon: <WorkspacePremiumIcon />, path: '/billing' },
    { text: 'Рефералы', icon: <CardGiftcardIcon />, path: '/referral' },
    ...(user?.role === 'admin'
      ? [{ text: 'Админка', icon: <AdminPanelSettingsIcon />, path: '/admin' }]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Live connection status — drives the "Online/Offline" chip (admin only).
  const [wsConnected, setWsConnected] = useState(false);
  useEffect(() => subscribeConnectionStatus(setWsConnected), []);

  // User menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const themeIcon =
    mode === 'light' ? <LightModeIcon /> : mode === 'dark' ? <DarkModeIcon /> : <AutoModeIcon />;
  const themeLabel =
    mode === 'light' ? 'Тема: светлая' : mode === 'dark' ? 'Тема: тёмная' : 'Тема: как в системе';

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Box
            component="img"
            src={`${import.meta.env.BASE_URL}logo.svg`}
            alt="VK Brand Promotion"
            sx={{ height: 34, flexGrow: 1, objectPosition: 'left', objectFit: 'contain', maxWidth: 220 }}
          />

          {user?.role === 'admin' && (
            <Tooltip
              title={
                wsConnected
                  ? 'Live-обновления подключены — счётчики обновляются автоматически'
                  : 'Нет соединения с сервером — счётчики обновятся после перезагрузки'
              }
            >
              <Chip
                size="small"
                icon={wsConnected ? <WifiIcon /> : <WifiOffIcon />}
                label={wsConnected ? 'В сети' : 'Не в сети'}
                color={wsConnected ? 'success' : 'default'}
                sx={{ mr: 2 }}
              />
            </Tooltip>
          )}

          <Tooltip title={`${themeLabel} — нажмите для смены`}>
            <IconButton color="inherit" onClick={cycleMode} sx={{ mr: 1 }}>
              {themeIcon}
            </IconButton>
          </Tooltip>

          <Tooltip title="Аккаунт">
            <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <PersonIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/profile');
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Профиль и пароль
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                handleLogout();
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Выйти
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  selected={location.pathname === item.path}
                  onClick={() => navigate(item.path)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, minHeight: '100vh' }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
